import { Dialog, DialogContent, Button as MuiButton, TextField } from "@mui/material"
import { useGoogleLogin } from "@react-oauth/google"
import axios from "axios"
import { GoogleAuthProvider, signInWithEmailAndPassword, User } from "firebase/auth"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate, useSearchParams } from "react-router-dom"
import { client } from "../.."
import { confirmMillInvite, millInviteError } from "../../api/millInvite"
import { getUserProfile } from "../../api/user"
import { Loader } from "../../components/Loader"
import Logo from "../../components/LogoPemPem"
import { useAppContext } from "../../contexts/AppContext"
import { checkEmail, checkPassword, isAndroid, showErrorMessage } from "../../helper/helper"
import { colorPrimary, colorRed } from "../../rsc/colors/colors"
import { ReactComponent as GoogleLogo } from "../../rsc/google_logo.svg"
import { ReactComponent as Check } from "../../rsc/ic_round_check.svg"
import { firebaseAuth } from "../login/firebaseInit"

type MessageType = {
    message: any,
    color: string
}

type Props = {
    enableAuth: () => void
    disableAuth: () => void
}

export function ConfirmAccount(props: Props) {

    const { setDisplayAndroidPopup } = useAppContext()
    const token = useSelector((state: any) => state.token.value)

    const navigate = useNavigate()

    const [param] = useSearchParams()

    const isConfirmSuccess = useRef<boolean | undefined>(undefined)

    const orgName = param.get("orgName")
    const inviterName = param.get("inviterName")
    const inviteeName = param.get("inviteeName")
    const orgId = param.get("orgId")
    const oob = param.get("oob")
    const orgType = param.get("orgType")
    const paramEmail = param.get("email")

    const [message, setMessage] = useState<MessageType>(null)
    const [password, setPassword] = useState("")
    const [email, setEmail] = useState("")
    const [isLoading, setIsLoading] = useState(true)
    const [isConfirmSucceed, setIsConfirmSucceed] = useState(false)

    useEffect(() => {
        props.disableAuth()
        console.log('disable auth listner')
        return () => {
            console.log('enable auth listner')
            props.enableAuth()
        }
    }, [])

    async function onUserLoggedIn() {
        const currentUser = firebaseAuth().currentUser
        const to = await currentUser.getIdToken()
        if (isConfirmSuccess.current === undefined && (paramEmail === currentUser.email)) {
            console.log('confirmaing address')
            const confirmMail = await confirmaMailAddress(to)
            if (confirmMail) {
                // The server wrote a role claim; the ID token must be re-minted before
                // it carries the new permissions.
                await currentUser.getIdToken(true)
                checkAndGoToMyPrice()
            } else {
                updateMessage({ message: t('user_unauthorised'), color: colorRed() })
            }
        } else {
            setIsLoading(false)
        }
    }

    function checkAndGoToMyPrice() {
        client.query({
            query: getUserProfile,
            context: {
                headers: {
                    "role": "usermill"
                }
            }
        }).then(result => {
            const orgs = (result.data.profile.profile.organisations ?? []).filter(o => !o.deleted_at)
            const org = orgs.find(o => o.id === parseInt(orgId))
            console.log('Checking orgs in my org', orgs, parseInt(orgId))
            if (org) {
                setIsLoading(false)
                if (isAndroid()) {
                    setDisplayAndroidPopup(true)
                }
                navigate('/explorer/my_prices')
            } else {
                console.log('token not ready yet...')
                setIsLoading(true)
                setTimeout(() => {
                    firebaseAuth().currentUser.getIdToken(true).then(token => {
                        checkAndGoToMyPrice()
                    }).catch(e => showErrorMessage(e))
                }, 5000)
            }
        }).catch(e => {
            setIsLoading(false)
        })
    }

    useEffect(() => {
        if (token) {
            onUserLoggedIn()
        } else {
            setIsLoading(false)
        }
    }, [token])

    const { t } = useTranslation()

    const onLoginClick = () => {
        isConfirmSuccess.current = undefined
        signInWithEmail(email, password, (user) => {
        }, (error) => {
            updateMessage({ message: error, color: colorRed() })
        })
    }

    const updateMessage = (message: MessageType) => {
        setMessage(message)
        setIsLoading(false)
    }

    const signInWithEmail = (email: string, password: string, success: (user: User) => void, failure: (error: any) => void) => {
        const isEmailValid = checkEmail(email) !== null
        const isPwdValid = checkPassword(password)
        if (!isEmailValid) {
            updateMessage({ message: t('email_pattern'), color: colorRed() })
        } else if (!isPwdValid) {
            updateMessage({ message: t('password_pattern'), color: colorRed() })
        } else {
            setIsLoading(true)
            signInWithEmailAndPassword(firebaseAuth(), email, password)
                .then(async (userCredential) => {
                }, (reason) => {
                    failure(reason)
                })
                .catch(async (error) => {
                    updateMessage({ message: error, color: colorRed() })
                });
        }
    }

    var userGoogleAccessToken = null

    function isConglomerate(): boolean {
        return orgType === "conglomerate"
    }

    const googleLogin = useGoogleLogin({
        onSuccess: async tokenResponse => {
            userGoogleAccessToken = tokenResponse.access_token
            // fetching userinfo can be done on the client or the server
            const userInfo = await axios
                .get('https://www.googleapis.com/oauth2/v3/userinfo', {
                    headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
                })
                .then(res => res.data);

            signInWithGoogle(userInfo)
        },
    })

    const signInWithGoogle = async (userInfo) => {
        setIsLoading(true)
        firebaseAuth().signInWithCredential(GoogleAuthProvider.credential(null, userGoogleAccessToken))
    }

    // Self-scoped: the endpoint resolves the invitation from the token's email plus
    // orgId + oob, so no uid or email is sent. A wrong or already-consumed oob is a 404.
    async function confirmaMailAddress(token: string): Promise<boolean> {
        try {
            await confirmMillInvite(parseInt(orgId), oob, token)
            isConfirmSuccess.current = true
            setIsConfirmSucceed(true)
            return true;
        } catch (e) {
            isConfirmSuccess.current = false
            showErrorMessage(millInviteError(e));
            return false;
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div style={{ height: "70%", display: "flex", alignItems: "center", flexDirection: "column" }}>
            <div style={{ height: "200px", width: "700px", display: "inline-block", marginTop: "30px" }}>
                <Logo />
            </div>
            <div className="loginDiv" style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", marginTop: "40px" }}>
                <div onKeyUp={(e) => { if (e.code === "Enter") { onLoginClick() } }}>
                    <p style={{ fontSize: "20px", lineHeight: "1.3em" }}>{isConglomerate() ? t('log_in_for_unilever', { invitee: inviteeName }) : t('log_in_for_mill', { invitee: inviteeName, inviter: inviterName, orgName: orgName })}</p>
                    <p style={{ fontSize: "20px", lineHeight: "1.3em", margin: "0px" }}>{isConglomerate() ? t('confirm_unilever_account_message') : t('confirm_account_message')}</p>
                    <div id="divInput" style={{ width: "320px" }}>
                        <div className="loginDiv">
                            <TextField style={{ marginTop: "30px", width: "100%" }} value={email} onChange={(e) => setEmail(e.target.value)} id="outlined-basic" label={t("enter_email")} variant="outlined" />
                        </div>
                        <div className="loginDiv">
                            <TextField style={{ width: "100%" }} type="password" value={password} onChange={(e) => setPassword(e.target.value)} id="outlined-basic" label={t("enter_password")} variant="outlined" />
                        </div>
                        {message ?
                            <div className="loginDiv">
                                <p style={{ color: message.color }}>{String(message.message)}</p>
                            </div>
                            : <></>}
                        <div className="loginDiv">
                            <MuiButton style={{ width: "100%", padding: "13px" }} variant="contained" onClick={() => onLoginClick()} id="login">{t("login")}</MuiButton>
                        </div>
                        <div className="loginDiv">
                            <div className="google-button" onClick={() => { isConfirmSuccess.current = undefined; googleLogin() }}>
                                <GoogleLogo style={{ display: "inline-block", marginRight: "10px", width: "32px", height: "32px" }}></GoogleLogo>
                                <p style={{ marginLeft: "10px", margin: "0px", padding: "0px", display: "inline-block" }}>{t('connect')}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <Dialog
                    disableEscapeKeyDown
                    open={isConfirmSucceed}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description">
                    <DialogContent>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                            <div style={{ display: "flex", flexDirection: "row", gap: "10px", alignItems: "center" }}>
                                <Check fill={colorPrimary()} style={{ height: "40px", width: "40px" }} />
                                <p style={{ fontSize: "20px", lineHeight: "30px" }}>{isConglomerate() ? t('join_unilever_success') : t('confirm_account_success')}</p>
                            </div>
                            <p style={{ fontSize: "20px", lineHeight: "30px" }}>{t('redirecting')}</p>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
            <Loader isLoading={isLoading} />
        </div>
    )
}